import { Component, Inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, finalize, forkJoin, map, of, Subscription, switchMap, tap } from 'rxjs';
import { Helper } from 'src/app/accountancy/utilities/classes/helper.class';
import { WaitHandler } from 'src/app/accountancy/utilities/classes/wait-handler.class';
import { LiquidationService } from '../liquidation-service/liquidation.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PromptMessage } from 'src/app/accountancy/utilities/classes/promp-message.class';
import { GlobalApiService } from 'src/app/accountancy/utilities/services/global-api/global-api.service';
import { AccConfirmationService } from 'src/app/accountancy/custom-components/acc-confirmation/acc-confirmation-service/acc-confirmation.service';

@Component({
  selector: 'app-liquidation-add-item-form',
  templateUrl: './liquidation-add-item-form.component.html',
  styleUrls: ['./liquidation-add-item-form.component.scss']
})
export class LiquidationAddItemFormComponent {

  public readonly waitHandler = new WaitHandler();
  public readonly Helper = Helper;
  public readonly prompt = new PromptMessage();
  public readonly fg = this._newFg();
  public readonly totalComputedAmount$ = new BehaviorSubject(0.0);

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: { liqId: number },
    private _matRef: MatDialogRef<LiquidationAddItemFormComponent>,
    private _liqS: LiquidationService,
    private _confirm: AccConfirmationService,
    private _gApiS: GlobalApiService,
  ) { }

  public onSubmit() {
    const formData = this.fg.value;

    if (Helper.getInvalidControls(this.fg, true).length) {
      this.prompt.set(Helper.errorMessage.requiredFields, 'danger');
      return;
    }

    if (!formData.Items?.length) {
      this.prompt.set("Please add at least 1 Item to continue.", 'danger');
      return;
    }

    const [year, month, date, day, h, m, s, ms] = Helper.date.dateDestruct(new Date());

    const subs: Subscription = of(formData).pipe(
      switchMap(rawData => {
        const files = rawData.Items?.map(item => item.Proof) ?? [];

        const getUploadURLObservers = files.map(file => {
          if (!file) return of(null);

          const fileName = `${year}${month}${date}${day}${h}${m}${s}${ms}_${file.name.trim()}`;

          return of(null).pipe(
            tap(() => this.waitHandler.newRequest()),
            switchMap(() => this._gApiS.getUploadFileUrl(file, fileName, "Receipts").pipe(
              finalize(() => this.waitHandler.finishRequest()),
              map(values => ({
                file: file,
                fileName: fileName,
                url: values.body?.presignedURL,
              }))
            ))
          );
        });

        return forkJoin(getUploadURLObservers);
      }),
      switchMap(values => {
        if (!values) return of(null);

        const putFileInServerObservers = values.map(file => {
          if (!file) return of(null);

          return of(null).pipe(
            tap(() => this.waitHandler.newRequest()),
            switchMap(() => {
              const url = file.url;

              if (!url) return of(null).pipe(
                finalize(() => this.waitHandler.finishRequest())
              );

              return this._gApiS.putFileInServer(url, file.file).pipe(
                finalize(() => this.waitHandler.finishRequest()),
                map(() => file),
              );
            })
          )
        })

        return forkJoin(putFileInServerObservers);
      }),
      switchMap(files => {
        if (!files) return of(null);

        const formattedItems = formData.Items?.map(item => {
          const fileName = `${year}${month}${date}${day}${h}${m}${s}${ms}_${item.Proof?.name.trim()}`;
          const file = files.find(f => f?.fileName === fileName);

          if (file) return ({ ...item, Proof: fileName, });
          return ({ ...item, Proof: '', });
        }) ?? [];

        const body = {
          ...formData,
          Items: formattedItems.map(item => ({ ...item, Remarks: item.Remarks || '' })),
        };

        return of(null).pipe(
          tap(() => this.waitHandler.newRequest()),
          switchMap(() => this._liqS.addLiquidationItem(this._data.liqId, body.Items).pipe(
            finalize(() => {
              this.waitHandler.finishRequest();
              this._matRef.close();
              subs.unsubscribe();
            }),
          ))
        )
      })

    ).subscribe();
  }

  public addItem(value?: ItemFormValueInterface) {
    this.fg.controls.Items.push(this._newFgItem(value));
  }

  public removeItemAt(index: number) {
    this._confirm.open({
      title: "Delete Item",
      message: `Are you sure you want to delete Item #${index + 1}`,
      onAnswer: (yes, matDialogRef, data) => {
        if (yes) this.fg.controls.Items.removeAt(index);
        matDialogRef.close();
      },
    });
  }

  public onUploadProof(e: Event, FormgGoupIndex: number) {
    const files = (e.target as HTMLInputElement).files as FileList;
    this.fg.controls.Items.at(FormgGoupIndex).controls.Proof.patchValue(files[0]);
  }

  private _newFgItem(value?: ItemFormValueInterface) {
    const handleComputeTotalAmount = () => {
      const computedAmount = this.fg.controls.Items.controls.reduce((c, n) => {
        const amount = n.controls.VatableSales.value ?? 0;
        const vat = n.controls.VAT.value ?? 0;

        return c + (amount + vat);
      }, 0);
      this.totalComputedAmount$.next(computedAmount);
    };

    const fcVatableSales = Helper.reactiveForms.control<number>(value?.VatableSales, [Validators.required]);
    fcVatableSales.valueChanges.subscribe(handleComputeTotalAmount);

    const fcVAT = Helper.reactiveForms.control<number>(value?.VAT);
    fcVAT.valueChanges.subscribe(handleComputeTotalAmount);

    return new FormGroup<ItemFormInterface>({
      VatableSales: fcVatableSales,
      VAT: fcVAT,
      Proof: Helper.reactiveForms.control<File>(value?.Proof, [Validators.required]),
      Remarks: Helper.reactiveForms.control<string>(value?.Remarks),
      Description: Helper.reactiveForms.control<string>(value?.Description, [Validators.required]),
    })
  }

  private _newFg() {
    return new FormGroup({
      Items: new FormArray<FormGroup<ItemFormInterface>>([this._newFgItem()]),
    });
  }

}

interface ItemFormInterface {
  VatableSales: FormControl<number | null | undefined>,
  VAT: FormControl<number | null | undefined>,
  Proof: FormControl<File | null | undefined>,
  Remarks: FormControl<string | null | undefined>,
  Description: FormControl<string | null | undefined>,
}

interface ItemFormValueInterface {
  VatableSales: number | null | undefined,
  VAT: number | null | undefined,
  Proof: File | null | undefined,
  Remarks: string | null | undefined,
  Description: string | null | undefined,
}
